/**
 * Container for the curated empty-query sections.
 *
 * Renders two sections on top of the global search modal when no
 * query has been entered: "Recently Viewed" (top 5 most-recently
 * interacted players, server-sourced) and "Upside Potential"
 * (listPlayerRecommendations with recommendationType=YOUNG_PLAYERS,
 * ranked by GPR descending).
 *
 * Recently Viewed is sourced from the server-side
 * `listRecentlyInteractedPlayers` query rather than a local
 * navigation store: the local `useNavigationTracking` hook referenced
 * in the SE-5018 ticket is analytics-only and does not retain visited
 * players, and the server query is already the source of truth for
 * this data.
 * @module features/global-search/components/GlobalSearchCuratedSections/GlobalSearchCuratedSectionsContainer
 */

import React, { memo, useMemo } from "react";

import { useAuth } from "@/features/auth";
import {
  SortField,
  type PlayerSearchPlayerFragment,
  useRecommendationsListPlayerRecommendationsQuery,
  useScoutingListRecentlyInteractedPlayersQuery,
} from "@/generated/graphql";

import GlobalSearchCuratedSectionsView from "./GlobalSearchCuratedSectionsView";

/** Max rows rendered per curated section */
const SECTION_LIMIT = 5;

/** Lookback window for the recently-interacted query */
const RECENT_SINCE_DAYS = 30;

/** Backend recommendation type for "Upside Potential" (per SE-5018 backend triage) */
const UPSIDE_RECOMMENDATION_TYPE = "YOUNG_PLAYERS";

/** Props for the curated sections container */
interface GlobalSearchCuratedSectionsContainerProps {
  /** Whether the parent modal is open — gates query firing */
  readonly isOpen: boolean;
  /** Called when a player row is pressed */
  readonly onPlayerPress: (player: PlayerSearchPlayerFragment) => void;
  /** Renders the trailing action slot (e.g. chevron) for each row */
  readonly renderAction: (
    player: PlayerSearchPlayerFragment
  ) => React.ReactNode;
}

/**
 * Coerces a partial Player object returned by curated-source queries into a
 * PlayerSearchPlayerFragment-compatible shape. Missing fields (nationality,
 * bioData on recommendations, isManual, etc.) are read as nullable by the row
 * container so casting is safe — we only need fields the row actually renders.
 * @param player - Player-like object from a curated query
 * @returns Object usable as PlayerSearchPlayerFragment by the row
 */
const adaptPlayer = (
  player: Record<string, unknown>
): PlayerSearchPlayerFragment =>
  player as unknown as PlayerSearchPlayerFragment;

/**
 * Container for the curated empty-query sections
 * @param root0 - Component props
 * @param root0.isOpen - Whether the parent modal is open
 * @param root0.onPlayerPress - Player row press handler
 * @param root0.renderAction - Action slot renderer per row
 */
const GlobalSearchCuratedSectionsContainer = ({
  isOpen,
  onPlayerPress,
  renderAction,
}: GlobalSearchCuratedSectionsContainerProps) => {
  const auth = useAuth();
  const hasAuth = Boolean(auth?.data?.accessToken);

  const { data: recentData, loading: recentLoading } =
    useScoutingListRecentlyInteractedPlayersQuery({
      variables: { limit: SECTION_LIMIT, sinceDays: RECENT_SINCE_DAYS },
      skip: !isOpen || !hasAuth,
      fetchPolicy: "cache-and-network",
    });

  const { data: upsideData, loading: upsideLoading } =
    useRecommendationsListPlayerRecommendationsQuery({
      variables: { recommendationType: UPSIDE_RECOMMENDATION_TYPE },
      skip: !isOpen || !hasAuth,
      fetchPolicy: "cache-and-network",
    });

  const recentPlayers = useMemo(
    (): readonly PlayerSearchPlayerFragment[] =>
      (recentData?.listRecentlyInteractedPlayers ?? [])
        .map(node => node.player)
        .filter((p): p is NonNullable<typeof p> => p != null)
        .slice(0, SECTION_LIMIT)
        .map(adaptPlayer),
    [recentData]
  );

  const upsidePlayers = useMemo(
    (): readonly PlayerSearchPlayerFragment[] =>
      (upsideData?.listPlayerRecommendations ?? [])
        .map(rec => rec.player)
        .filter((p): p is NonNullable<typeof p> => p != null)
        .slice(0, SECTION_LIMIT)
        .map(adaptPlayer),
    [upsideData]
  );

  return (
    <GlobalSearchCuratedSectionsView
      recentPlayers={recentPlayers}
      upsidePlayers={upsidePlayers}
      recentLoading={recentLoading && recentPlayers.length === 0}
      upsideLoading={upsideLoading && upsidePlayers.length === 0}
      onPlayerPress={onPlayerPress}
      renderAction={renderAction}
      effectiveSortBy={SortField.Gpr}
      sortFieldLabel="GPR"
    />
  );
};

export default memo(GlobalSearchCuratedSectionsContainer);
